import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

const alvornEntryGraphics = new Map([
  ['script', ['Script Grafik']],
  ['character', ['Kanji Grafik', 'Karakter Grafik', 'Karaket Grafik']],
]);
const alvornEntryColours = new Map([
  ['gri', 'Gri'],
  ['siyah', 'Siyah'],
  ['haki', 'Haki'],
  ['lacivert', 'Lacivert'],
  ['beyaz', 'Beyaz'],
]);
const alvornFixedFamilyHandles = new Map([
  ['script-full-set', 'script'],
  ['character-full-set', 'character'],
]);
/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #variantChangeHandler = this.variantChanged.bind(this);

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#initializeFieldsetState();

    this.addEventListener('change', this.#variantChangeHandler);
    this.#resizeObserver.observe(this);
    this.#applyAlvornEntryStateFromUrl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#variantChangeHandler);
    this.#resizeObserver.disconnect();
  }

  #applyAlvornEntryStateFromUrl() {
    if (this.dataset.templateProductMatch !== 'true') return;

    const url = new URL(window.location.href);
    const requestedVariantId = url.searchParams.get('variant');
    const selectedVariantId = this.dataset.alvornSelectedVariantId;
    if (requestedVariantId && selectedVariantId && requestedVariantId === selectedVariantId) {
      this.dataset.alvornEntryStateApplied = 'true';
      return;
    }

    const graphic = url.searchParams.get('graphic');
    const colour = url.searchParams.get('colour');
    if (graphic == null && colour == null) return;

    if (this.dataset.alvornEntryStateApplied === 'true') return;

    this.dataset.alvornEntryStateApplied = 'true';
    this.applyAlvornEntryState({ graphic, colour });
  }

  /**
   * Applies only a validated catalogue graphic/colour entry state. Explicit size
   * options remain untouched and the final colour change creates one native
   * section update for the complete partial selection.
   * @param {{ graphic?: string | null, colour?: string | null }} entryState
   * @returns {boolean}
   */
  applyAlvornEntryState(entryState = { graphic: null, colour: null }) {
    const { graphic = null, colour = null } = entryState;
    const optionElements = /** @type {HTMLElement[]} */ (Array.from(
      this.querySelectorAll('.variant-picker__form > .variant-option[data-alvorn-option-kind]')
    ));
    const graphicOption = optionElements.find(
      (option) => option instanceof HTMLElement && option.dataset.alvornOptionKind === 'graphic'
    );
    const colourOption = optionElements.find(
      (option) => option instanceof HTMLElement && option.dataset.alvornOptionKind === 'colour'
    );
    const declaredGraphicOption = this.dataset.alvornHasGraphicOption;
    const hasGraphicOption = Boolean(graphicOption);

    if (
      !colourOption ||
      !['true', 'false'].includes(declaredGraphicOption ?? '') ||
      (declaredGraphicOption === 'true') !== hasGraphicOption
    ) {
      this.#announceAlvornEntryStateRejection();
      return false;
    }

    const productHandle = this.dataset.alvornProductHandle ?? '';
    const declaredFixedFamily = this.dataset.alvornFixedGraphicFamily ?? '';
    const handleFixedFamily = alvornFixedFamilyHandles.get(productHandle) ?? '';
    const isFixedFamily = declaredFixedFamily !== '' || handleFixedFamily !== '';
    if (
      isFixedFamily &&
      (declaredFixedFamily !== handleFixedFamily || hasGraphicOption)
    ) {
      this.#announceAlvornEntryStateRejection();
      return false;
    }

    const colourKey = String(colour ?? '').trim().toLocaleLowerCase('tr-TR');
    const colourLabel = alvornEntryColours.get(colourKey);
    const colourSelection = colourLabel ? this.#findAlvornEntrySelection(colourOption, colourLabel) : null;
    if (!colourSelection) {
      this.#announceAlvornEntryStateRejection();
      return false;
    }

    let graphicSelection = null;
    if (hasGraphicOption) {
      const graphicKey = String(graphic ?? '').trim().toLocaleLowerCase('tr-TR');
      const graphicLabel = alvornEntryGraphics.get(graphicKey);
      graphicSelection = graphicLabel
        ? this.#findAlvornEntrySelection(graphicOption, graphicLabel)
        : null;
      if (!graphicSelection) {
        this.#announceAlvornEntryStateRejection();
        return false;
      }
    }

    if (graphicSelection) this.#commitAlvornEntrySelection(graphicSelection);
    this.#commitAlvornEntrySelection(colourSelection);
    this.#initializeFieldsetState();
    colourSelection.target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /**
   * @param {HTMLElement} optionElement
   * @param {string | string[]} value
   * @returns {{ target: HTMLElement, value: string } | null}
   */
  #findAlvornEntrySelection(optionElement, value) {
    if (!(optionElement instanceof HTMLElement)) return null;
    const values = Array.isArray(value) ? value : [value];
    if (!values.every((candidate) => typeof candidate === 'string')) return null;

    const radios = /** @type {HTMLInputElement[]} */ (
      Array.from(optionElement.querySelectorAll('input[type="radio"]'))
    );
    if (radios.length > 0) {
      const matches = radios.filter(
        (input) => input instanceof HTMLInputElement && !input.disabled && values.includes(input.value)
      );
      const radio = matches.length === 1 ? matches[0] : null;
      return radio instanceof HTMLInputElement ? { target: radio, value: radio.value } : null;
    }

    const select = optionElement.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      const matches = Array.from(select.options).filter((candidate) => !candidate.disabled && values.includes(candidate.value));
      const option = matches.length === 1 ? matches[0] : null;
      return option ? { target: select, value: option.value } : null;
    }

    return values.includes(optionElement.dataset.optionValue ?? '')
      ? { target: optionElement, value: optionElement.dataset.optionValue ?? '' }
      : null;
  }

  /** @param {{ target: HTMLElement, value: string }} selection */
  #commitAlvornEntrySelection({ target, value }) {
    if (target instanceof HTMLSelectElement) target.value = value;
    this.updateSelectedOption(target);
  }

  #announceAlvornEntryStateRejection() {
    const message = this.dataset.alvornEntryAnnouncement?.trim();
    if (!message) return;

    const productId = this.dataset.productId;
    const productForms = /** @type {HTMLElement[]} */ (
      Array.from(document.querySelectorAll('product-form-component'))
    );
    const productForm = productForms.find(
      (candidate) => candidate instanceof HTMLElement && (!productId || candidate.dataset.productId === productId)
    );
    const liveRegion = productForm?.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = message;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;
    const requiresExactVariantResolution = this.#explicitSizeFieldsets().length > 0;

    if (isOnProductPage) {
      if (variantId && !requiresExactVariantResolution) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      const radioGroup = target.closest('fieldset')?.querySelectorAll('input[type="radio"]') ?? [];
      for (const radio of radioGroup) {
        if (radio !== target) radio.checked = false;
        radio.removeAttribute('checked');
      }
      target.checked = true;
      target.setAttribute('checked', '');
      target
        .closest('fieldset')
        ?.querySelector('.variant-option__selected-value')
        ?.replaceChildren(document.createTextNode(target.dataset.alvornDisplayValue ?? target.value));
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const requestAbortController = new AbortController();
    this.#abortController = requestAbortController;
    let responseHtml = new DOMParser().parseFromString('', 'text/html');

    const isCurrentRequest = () =>
      this.#abortController === requestAbortController && requestAbortController.signal?.aborted !== true;

    /**
     * @param {any} resource
     * @param {NewProduct | undefined} [newProduct]
     */
    const dispatchTerminalUpdate = (resource, newProduct = undefined) => {
      if (!isCurrentRequest() || !this.selectedOptionId) return;
      this.dispatchEvent(
        new VariantUpdateEvent(resource, this.selectedOptionId, {
          html: responseHtml,
          productId: this.dataset.productId ?? '',
          newProduct,
        })
      );
    };

    fetch(requestUrl, { signal: requestAbortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Variant section request failed with status ${response.status}`);
        return response.text();
      })
      .then(async (responseText) => {
        if (!isCurrentRequest()) return;
        this.#pendingRequestUrl = undefined;
        responseHtml = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        responseHtml.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = responseHtml.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          dispatchTerminalUpdate(null);
          return;
        }

        let newProduct;
        let resolvedResource = JSON.parse(textContent);

        if (morphElementSelector === 'main') {
          this.updateMain(responseHtml);
        } else if (morphElementSelector) {
          this.updateElement(responseHtml, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(responseHtml);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        if (this.#explicitSizeFieldsets().length > 0) {
          if (!this.#hasCompleteExplicitSizeSelection()) {
            this.#replaceProductPageVariant(null);
            dispatchTerminalUpdate(null, newProduct);
            return;
          }

          const matrixVariant = this.#resolveExactVariant();
          if (!matrixVariant?.id) {
            dispatchTerminalUpdate(null, newProduct);
            return;
          }

          const exactResponse = await fetch(this.#buildExactVariantRequestUrl(requestUrl, matrixVariant.id), {
            signal: requestAbortController.signal,
          });
          if (!exactResponse.ok) {
            throw new Error(`Exact variant request failed with status ${exactResponse.status}`);
          }
          const exactResponseText = await exactResponse.text();
          if (!isCurrentRequest()) return;

          const exactResponseHtml = new DOMParser().parseFromString(exactResponseText, 'text/html');
          exactResponseHtml.querySelector('overflow-list[defer]')?.removeAttribute('defer');
          const exactTextContent = exactResponseHtml.querySelector(
            'variant-picker script[type="application/json"]'
          )?.textContent;
          if (!exactTextContent) {
            dispatchTerminalUpdate(null, newProduct);
            return;
          }

          const exactResource = JSON.parse(exactTextContent);
          if (String(exactResource?.id ?? '') !== String(matrixVariant.id)) {
            dispatchTerminalUpdate(null, newProduct);
            return;
          }

          resolvedResource = exactResource;
          responseHtml = exactResponseHtml;
          this.#replaceProductPageVariant(exactResource.id);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress.
        dispatchTerminalUpdate(resolvedResource, newProduct);
      })
      .catch((error) => {
        if (!isCurrentRequest() || error.name === 'AbortError') return;
        this.#pendingRequestUrl = undefined;
        dispatchTerminalUpdate(null);
      });
  }

  #buildExactVariantRequestUrl(requestUrl, variantId) {
    const exactUrl = new URL(requestUrl, window.location.href);
    exactUrl.searchParams.delete('option_values');
    exactUrl.searchParams.set('variant', String(variantId));

    return /^https?:\/\//i.test(requestUrl)
      ? exactUrl.toString()
      : `${exactUrl.pathname}${exactUrl.search}${exactUrl.hash}`;
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const preservedSelections = this.#captureSelectionState();
    const entryStateApplied = this.dataset.alvornEntryStateApplied === 'true';
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    if (entryStateApplied) this.dataset.alvornEntryStateApplied = 'true';
    this.#restoreSelectionState(preservedSelections);
    this.#clearMissingExplicitSizeSelections(preservedSelections);
    this.#initializeFieldsetState();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      '.variant-picker__form select option[selected], .variant-picker__form fieldset input:checked'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const values = [];
    const optionElements = this.querySelectorAll('.variant-picker__form > .variant-option');

    for (const optionElement of optionElements) {
      const radio = Array.from(optionElement.querySelectorAll('input[type="radio"]')).find(
        (input) => input.checked || input.hasAttribute('checked')
      );
      if (radio instanceof HTMLInputElement) {
        if (!radio.dataset.optionValueId) throw new Error('No option value ID found');
        values.push(radio.dataset.optionValueId);
        continue;
      }

      const select = optionElement.querySelector('select');
      if (select instanceof HTMLSelectElement && select.value) {
        const selectedOption = Array.from(select.options).find((option) => option.value === select.value);
        if (!selectedOption?.dataset.optionValueId) throw new Error('No option value ID found');
        values.push(selectedOption.dataset.optionValueId);
        continue;
      }

      if (optionElement.dataset.optionValueId) values.push(optionElement.dataset.optionValueId);
    }

    return values;
  }

  #explicitSizeFieldsets() {
    return this.querySelectorAll('[data-explicit-selection-required="true"]');
  }

  #hasCompleteExplicitSizeSelection() {
    return Array.from(this.#explicitSizeFieldsets()).every((fieldset) => {
      const select = fieldset.querySelector('select');
      if (select) return !select.disabled && select.value !== '';
      return Array.from(fieldset.querySelectorAll('input[type="radio"]')).some(
        (input) => (input.checked || input.hasAttribute('checked')) && !input.disabled
      );
    });
  }

  #initializeFieldsetState() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios[fieldsetIndex] = radios;
      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );
      this.#checkedIndices[fieldsetIndex] = initialCheckedIndex === -1 ? [] : [initialCheckedIndex];
    });
  }

  /**
   * Captures the customer's current option choices before Shopify's section HTML is morphed.
   * @returns {Map<string, { optionValueId: string, value: string }>}
   */
  #captureSelectionState() {
    const selections = new Map();
    const optionElements = this.querySelectorAll('.variant-picker__form > .variant-option[data-option-position]');

    for (const optionElement of optionElements) {
      const position = optionElement.dataset.optionPosition;
      if (!position) continue;

      const radio = Array.from(optionElement.querySelectorAll('input[type="radio"]')).find(
        (input) => input.checked || input.hasAttribute('checked')
      );
      if (radio instanceof HTMLInputElement) {
        selections.set(position, {
          optionValueId: radio.dataset.optionValueId ?? '',
          value: radio.value,
        });
        continue;
      }

      const select = optionElement.querySelector('select');
      if (select instanceof HTMLSelectElement && select.value) {
        const selectedOption = Array.from(select.options).find((option) => option.value === select.value);
        selections.set(position, {
          optionValueId: selectedOption?.dataset.optionValueId ?? '',
          value: select.value,
        });
      }
    }

    return selections;
  }

  /**
   * Restores only choices that still exist and remain available in the refreshed section.
   * @param {Map<string, { optionValueId: string, value: string }>} selections
   */
  #restoreSelectionState(selections) {
    const optionElements = this.querySelectorAll('.variant-picker__form > .variant-option[data-option-position]');

    for (const optionElement of optionElements) {
      const selection = selections.get(optionElement.dataset.optionPosition ?? '');
      if (!selection) continue;

      const radios = Array.from(optionElement.querySelectorAll('input[type="radio"]'));
      if (radios.length > 0) {
        for (const radio of radios) {
          radio.checked = false;
          radio.removeAttribute('checked');
          radio.dataset.currentChecked = 'false';
          radio.dataset.previousChecked = 'false';
        }
        const matchingRadio = radios.find(
          (radio) =>
            !radio.disabled &&
            ((selection.optionValueId && radio.dataset.optionValueId === selection.optionValueId) ||
              radio.value === selection.value)
        );
        if (!matchingRadio) continue;
        matchingRadio.checked = true;
        matchingRadio.setAttribute('checked', '');
        matchingRadio.dataset.currentChecked = 'true';
        optionElement
          .querySelector('.variant-option__selected-value')
          ?.replaceChildren(document.createTextNode(matchingRadio.dataset.alvornDisplayValue ?? matchingRadio.value));
        continue;
      }

      const select = optionElement.querySelector('select');
      if (!(select instanceof HTMLSelectElement)) continue;
      const matchingOption = Array.from(select.options).find(
        (option) =>
          !option.disabled &&
          ((selection.optionValueId && option.dataset.optionValueId === selection.optionValueId) ||
            option.value === selection.value)
      );
      if (!matchingOption) continue;
      for (const option of select.options) option.removeAttribute('selected');
      matchingOption.setAttribute('selected', 'selected');
      select.value = matchingOption.value;
    }
  }

  /**
   * Shopify contextual option responses can carry a first available variant.
   * Preserve an intentionally blank size when the customer has not selected one.
   * @param {Map<string, { optionValueId: string, value: string }>} selections
   */
  #clearMissingExplicitSizeSelections(selections) {
    for (const fieldset of this.#explicitSizeFieldsets()) {
      const position = fieldset.dataset.optionPosition;
      if (position && selections.has(position)) continue;

      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      for (const radio of radios) {
        radio.checked = false;
        radio.removeAttribute('checked');
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }

      const select = fieldset.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        for (const option of select.options) option.removeAttribute('selected');
        const placeholder = Array.from(select.options).find((option) => option.value === '');
        if (placeholder) placeholder.setAttribute('selected', 'selected');
        select.value = '';
      }

      const emptyLabel = fieldset.dataset.alvornUnselectedLabel;
      if (emptyLabel) {
        fieldset
          .querySelector('.variant-option__selected-value')
          ?.replaceChildren(document.createTextNode(emptyLabel));
      }
    }
  }

  /** @returns {Array<string | null>} */
  #selectedOptionLabels() {
    return Array.from(this.querySelectorAll('.variant-picker__form > .variant-option')).map((optionElement) => {
      const radio = Array.from(optionElement.querySelectorAll('input[type="radio"]')).find(
        (input) => input.checked || input.hasAttribute('checked')
      );
      if (radio instanceof HTMLInputElement) return radio.value;

      const select = optionElement.querySelector('select');
      if (select instanceof HTMLSelectElement) return select.value || null;

      return optionElement.dataset.optionValue || null;
    });
  }

  #resolveExactVariant() {
    const selectedOptions = this.#selectedOptionLabels();
    if (selectedOptions.length === 0 || selectedOptions.some((value) => value == null || value === '')) return null;

    const matrixText = this.querySelector('script[data-alvorn-variant-matrix]')?.textContent;
    if (!matrixText) return null;

    try {
      const variants = JSON.parse(matrixText);
      if (!Array.isArray(variants)) return null;
      return (
        variants.find(
          (variant) =>
            Array.isArray(variant.options) &&
            variant.options.length === selectedOptions.length &&
            variant.options.every((value, index) => String(value) === selectedOptions[index])
        ) ?? null
      );
    } catch (_error) {
      return null;
    }
  }

  #replaceProductPageVariant(variantId) {
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');
    if (!isOnProductPage) return;

    const url = new URL(window.location.href);
    if (variantId) url.searchParams.set('variant', String(variantId));
    else url.searchParams.delete('variant');
    if (url.href === window.location.href) return;

    yieldToMainThread().then(() => {
      history.replaceState({}, '', url.toString());
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
